import { useNavigate } from '@tanstack/react-router';
import { isNil } from 'lodash';
import { useCallback, useEffect } from 'react';
import { RETURN_CHANNEL_SLUG_QUERY } from '@shared/scenes/cards/form/update/constants';
import { i18n } from '@shared/util/i18n';
import { RouteName } from './routeParams';
import {
  Location,
  useNavigationBeforeLeave,
  useNavigationFocus,
  useNavigator,
} from './useNavigator';

export function useUnsavedChangesNavigationWarning(
  enabled: boolean,
  /**
   * Navigations to allow without asking, on top of the ones this hook already allows. For a form
   * whose own modals are child routes: those navigations change the URL without leaving the form, so
   * there is nothing to lose and nothing to warn about.
   */
  isAllowed?: (nextLocation: Location) => boolean,
) {
  const focused = useNavigationFocus();
  const callback = useCallback(
    (nextLocation: Location): boolean | Promise<boolean> => {
      const isSubformRedirect = !isNil(nextLocation?.params?.[RETURN_CHANNEL_SLUG_QUERY]);

      if (isSubformRedirect || !enabled || !focused || isAllowed?.(nextLocation) === true) {
        return true;
      }

      // return false to prevent navigation. can't use MBAlert here because this
      // callback needs to be synchronous

      // eslint-disable-next-line no-alert
      return window.confirm(i18n('unsavedChangesWarning.web.message'));
    },
    [enabled, focused, isAllowed],
  );

  useNavigationBeforeLeave(callback);

  // Add beforeunload handler to catch browser navigation (window.location, tab close, refresh, etc.)
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Modern browsers ignore the custom message and show their own
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled]);
}

export function useNavigateToRoute(routeName: RouteName): VoidFunction {
  const { navigate } = useNavigator();
  return useCallback(() => navigate(routeName), [navigate, routeName]);
}

interface BuilderLinkProps {
  route: string;
  isNested?: boolean;
  includeOverlay?: boolean;
  includeMenu?: boolean;
}

/**
 * Hook for navigating to the builder app with proper history management.
 *
 * When a channelSlug is provided, known routes navigate in-app via TanStack Router
 * instead of doing a full page load to the standalone builder app.
 *
 * In-app routes:
 *   - /field → /$channelSlug/builder/$workflowTemplateId/field
 *   - /subform → /$channelSlug/builder/$workflowTemplateId/subform
 *   - /automation → /$channelSlug/builder/$workflowTemplateId/automation
 *   - /template/{i,e,c} → /$channelSlug/builder/$workflowTemplateId/template/{i,e,c}
 *
 * @param workflowTemplateId - The workflow template ID to navigate to in builder
 * @param channelSlug - The channel slug, required for in-app navigation
 * @returns A callback function that accepts props and navigates to builder
 */
export function useBuilderLink(workflowTemplateId?: string, channelSlug?: string) {
  const navigate = useNavigate();

  return useCallback(
    ({
      route,
      isNested = false,
      includeOverlay = false,
      includeMenu = !isNested,
    }: BuilderLinkProps) => {
      if (!workflowTemplateId) {
        console.error('useBuilderLink: workflowTemplateId is required');
        return;
      }

      const normalizedRoute = route.startsWith('/') ? route.slice(1) : route;

      // In-app navigation when we have a channel context
      if (channelSlug) {
        if (normalizedRoute === 'field') {
          navigate({
            to: '/$channelSlug/builder/$workflowTemplateId/field',
            params: { channelSlug, workflowTemplateId },
          });
          return;
        }

        if (normalizedRoute === 'subform') {
          navigate({
            to: '/$channelSlug/builder/$workflowTemplateId/subform',
            params: { channelSlug, workflowTemplateId },
          });
          return;
        }

        if (normalizedRoute === 'automation') {
          navigate({
            to: '/$channelSlug/builder/$workflowTemplateId/automation',
            params: { channelSlug, workflowTemplateId },
          });
          return;
        }

        if (normalizedRoute === 'template/i') {
          navigate({
            to: '/$channelSlug/builder/$workflowTemplateId/template/i',
            params: { channelSlug, workflowTemplateId },
            state: prev => ({
              ...prev,
              builderReturnTo: window.location.pathname + window.location.search,
            }),
          });
          return;
        }

        if (normalizedRoute === 'template/e') {
          navigate({
            to: '/$channelSlug/builder/$workflowTemplateId/template/e',
            params: { channelSlug, workflowTemplateId },
          });
          return;
        }

        if (normalizedRoute === 'template/c') {
          navigate({
            to: '/$channelSlug/builder/$workflowTemplateId/template/c',
            params: { channelSlug, workflowTemplateId },
            state: prev => ({
              ...prev,
              builderReturnTo: window.location.pathname + window.location.search,
            }),
          });
          return;
        }
      }

      // Fall back to full-page navigation to the standalone builder app
      const currentUrl = window.location.href;
      window.history.pushState(null, '', currentUrl);
      const queryParams = new URLSearchParams();
      queryParams.set('returnTo', currentUrl);
      queryParams.set('nested', String(isNested));
      queryParams.set('overlay', String(includeOverlay));
      queryParams.set('menu', String(includeMenu));
      const BUILDER_URL = import.meta.env.VITE_BUILDER_APP_URL;
      window.location.href = `${BUILDER_URL}/${workflowTemplateId}/${normalizedRoute}?${queryParams.toString()}`;
    },
    [workflowTemplateId, channelSlug, navigate],
  );
}
